import { getApiOrigin } from "@utils/apiOrigin";

export interface AddFacilityByTypeSettingsResponse {
  success?: boolean;
  message?: string;
  response?: {
    enabled?: boolean;
  };
}

export interface AddFacilityByTypeSettings {
  enabled: boolean;
}

const ADD_FACILITY_BY_TYPE_SETTINGS_PATH =
  "/controls/add-facility-by-type/settings";

let cachedSettings: AddFacilityByTypeSettings | null = null;
let pendingSettingsPromise: Promise<AddFacilityByTypeSettings> | null = null;

/**
 * Whether Rules Controls Features shows Add-facility-by-type on My Facility
 * empty search. Default true when the endpoint is unreachable.
 */
export async function getAddFacilityByTypeSettings(
  forceRefresh = false,
): Promise<AddFacilityByTypeSettings> {
  if (!forceRefresh && cachedSettings) {
    return cachedSettings;
  }

  if (!forceRefresh && pendingSettingsPromise) {
    return pendingSettingsPromise;
  }

  pendingSettingsPromise = (async () => {
    const baseUrl = getApiOrigin();
    const candidateUrls: string[] = [];
    const addCandidate = (url: string) => {
      if (!candidateUrls.includes(url)) {
        candidateUrls.push(url);
      }
    };

    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      addCandidate(`${normalizedBase}${ADD_FACILITY_BY_TYPE_SETTINGS_PATH}`);
      addCandidate(
        `${normalizedBase}/api/v1${ADD_FACILITY_BY_TYPE_SETTINGS_PATH}`,
      );
    }

    addCandidate(ADD_FACILITY_BY_TYPE_SETTINGS_PATH);
    addCandidate(`/api/v1${ADD_FACILITY_BY_TYPE_SETTINGS_PATH}`);

    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          continue;
        }
        const data =
          (await response.json()) as AddFacilityByTypeSettingsResponse;
        const settings: AddFacilityByTypeSettings = {
          enabled: data?.response?.enabled !== false,
        };
        cachedSettings = settings;
        return settings;
      } catch {
        // Try next candidate URL.
      }
    }

    return { enabled: true };
  })();

  try {
    return await pendingSettingsPromise;
  } finally {
    pendingSettingsPromise = null;
  }
}

export function resetAddFacilityByTypeSettingsCacheForTests(): void {
  cachedSettings = null;
  pendingSettingsPromise = null;
}
